import { CommonModule } from '@angular/common'
import { Component, Input, OnDestroy, OnInit, WritableSignal, signal } from '@angular/core'
import { FormControl, ReactiveFormsModule } from '@angular/forms'
import { MatIconModule } from '@angular/material/icon'
import { MatMenuModule } from '@angular/material/menu'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { NgScrollbar, NgScrollbarModule } from 'ngx-scrollbar'
import { Subject, debounceTime, distinctUntilChanged, from, takeUntil } from 'rxjs'
import { Option } from './fc-search-select.type'
import { listAnimation } from '../../animations'

@Component({
    selector: 'fc-search-select',
    templateUrl: './fc-search-select.component.html',
    styleUrls: ['./fc-search-select.component.scss'],
    animations: [
        listAnimation
    ],
    imports: [
        CommonModule,
        MatIconModule,
        MatMenuModule,
        MatProgressBarModule,
        NgScrollbarModule,
        ReactiveFormsModule,
    ]
})
export class FcSearchSelectComponent implements OnInit, OnDestroy {
  public searchNameInputControl = new FormControl()

  @Input({ required: true })
  public placeholder!: string

  @Input({ required: true })
  public allOptionsLabel!: string

  @Input({ required: true })
  public noOptionsLabel!: string

  @Input({ required: true })
  public control!: any

  @Input({ required: true })
  public service!: any

  @Input({ required: true })
  public searchProperty!: string

  public isLoading = signal(true)
  public shouldLoadMoreItems = true
  public options: WritableSignal<Option[]> = signal([])
  public readonly destroy$ = new Subject<void>()

  public async ngOnInit(): Promise<void> {
    this.service.limit.set(10)
    this.service.offset.set(0)

    await this.search()
    this.searchNameInputControl.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(async () => {
      await this.search()
    })
  }

  public isSelected(option: Option): boolean {
    return this.control.value?.id === option.id
  }

  public ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  public trackByFn(index: number): number {
    return index
  }

  public selectOption(option: Option): void {
    this.control.setValue(option)
  }

  public clearControl(): void {
    this.control.setValue(null)
  }

  public onScrollbarUpdate(scrollbar: NgScrollbar) {
    scrollbar.scrolled.pipe(takeUntil(this.destroy$)).subscribe((event: any) => {
      this.onScroll(event)
    })
  }

  public onScroll(event: any): void {
    const scrollPosition: number = event.target.scrollTop
    const scrollHeight: number = event.target.scrollHeight
    const clientHeight: number = event.target.clientHeight
    const isAtBottom: boolean = scrollPosition + clientHeight >= scrollHeight

    if (!isAtBottom || this.isLoading() || !this.shouldLoadMoreItems) {
      return
    }

    this.isLoading.set(true)
    this.service?.offset.update((value: number) => value + (this.service?.limit() as number))

    from(this.service?.searchPage()).pipe(
      debounceTime(500),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(async () => {
      await this.search(true)
    })
  }

  private async search(isScrolling?: boolean): Promise<void> {
    this.isLoading.set(true)
    const value = this.searchNameInputControl.value

    this.service.filterForm.get(this.searchProperty)?.setValue(value || null)
    this.service.filterForm.get('archived')?.setValue(false)
    const { data } = await this.service.searchPage()
    const [firstProperty]: any = Object.values(data)

    const options = isScrolling
      ? [...this.options(), ...firstProperty.items]
      : firstProperty.items

    this.options.set(options)
    this.shouldLoadMoreItems = this.options().length !== firstProperty?.totalCount

    this.isLoading.set(false)
  }
}
